import React, { useState, useEffect, useCallback, useRef } from 'react';
import VoiceOriginContext from '../context/VoiceOriginContext';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner, faTrash, faComments, faCopy, faShare
} from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import { newUploadId, requestUploadCancellation } from '../utils/uploadCancellation';
import { getSocket, onSocket } from '../utils/socket';
import { tickState } from '../utils/receipts';
import MessageBubble from './chat/MessageBubble';
import MessageComposer from './chat/MessageComposer';
import FileDropZone from './FileDropZone';
import MessageInfo from './chat/MessageInfo';
import ForwardModal from './chat/ForwardModal';
import TypingIndicator from './chat/TypingIndicator';
import { SelectionBar, applyReactionLocal, copyText, transcript } from './chat/MessageActions';
import '../styles/messenger.css';
import '../styles/discussion.css';

/**
 * A task's discussion, drawn and behaving like a chat.
 *
 * The thread is the chat's own components — MessageBubble, MessageComposer,
 * MessageInfo, ForwardModal, TypingIndicator — fed task comments reshaped into
 * chat messages (toMessage). So it reads the way the Chats page does: bubbles,
 * day separators, ticks (sent, delivered, read), message info, replies,
 * reactions, @mentions, forwarding to a chat, select/copy, typing, and a
 * message that appears the instant Send is pressed. Calling is the one chat
 * feature a task thread has no use for.
 *
 * Who the ticks wait on is the task's people — its assignees and whoever
 * assigned it — the same people who are notified of every message.
 *
 * `basePath` is what lets a recurring schedule have its own thread: the routes
 * are identical in shape, only mounted under /tasks/recurring instead. The
 * socket room is keyed by the id either way, so live updates need no change.
 */

const PRIVILEGED = ['ADMIN', 'HR'];
const RECEIPT_EVENTS = ['task:comment-read', 'task:comment-delivered'];

const daySeparator = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** A task comment in the shape the chat components render. */
const toMessage = (c) => ({
    ...c,
    text: c.message || '',
    sender: c.author,
    replyTo: c.replyTo?.commentId
        ? { messageId: c.replyTo.commentId, text: c.replyTo.text, senderName: c.replyTo.authorName }
        : undefined,
    // Comments older than `type` were always images.
    attachments: (c.attachments || []).map((a) => ({ ...a, type: a.type || 'image' })),
    mentions: c.mentions || [],
    readBy: c.readBy || [],
    deliveredTo: c.deliveredTo || [],
    reactions: c.reactions || []
});

/** What a message reads as when copied, words or the kind of attachment. */
const snippetOf = (m) => {
    if (m.text) return m.text;
    const kind = m.attachments?.[0]?.type || 'image';
    return {
        audio: '<Voice note>', video: '<Recording>', image: '<Photo>'
    }[kind] || `<${m.attachments?.[0]?.fileName || 'File'}>`;
};

const idOf = (v) => String(v?._id || v || '');

const TaskDiscussion = ({ taskId, currentUserId, basePath = '/tasks', title = 'Discussion' }) => {
    const me = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { return {}; }
    })();
    const myId = String(currentUserId || me._id || me.id || '');
    const myRole = me.role;

    const [messages, setMessages] = useState([]);
    const composerRef = useRef(null);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);

    const [replyTo, setReplyTo] = useState(null);
    const [editing, setEditing] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [forwardIds, setForwardIds] = useState(null);
    const [infoFor, setInfoFor] = useState(null);
    const [typingPeople, setTypingPeople] = useState([]);
    const typingTimers = useRef(new Map());

    const threadRef = useRef(null);
    const bottomRef = useRef(null);
    const atBottomRef = useRef(true);
    const lastCountRef = useRef(0);

    const base = `${basePath}/${taskId}/comments`;

    /* ------------------------------ loading ------------------------------ */

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            api.get(base),
            // Only used for ticks and @mentions; a thread still works without it.
            api.get(`${base}/people`).catch(() => ({ data: [] }))
        ])
            .then(([comments, participants]) => {
                if (cancelled) return;
                setMessages((comments.data || []).map(toMessage));
                setPeople(participants.data || []);
            })
            .catch((err) => console.error('Could not load discussion', err))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [base]);

    /* ------------------------------ realtime ------------------------------ */

    useEffect(() => {
        const socket = getSocket();
        if (!socket) return undefined;

        socket.emit('task:join', taskId);
        const mineThread = (tid) => String(tid) === String(taskId);

        const offNew = onSocket('task:comment', (incoming) => {
            if (!mineThread(incoming.taskId)) return;
            const msg = toMessage(incoming);
            setMessages((prev) => {
                if (prev.some((m) => m._id === msg._id)) return prev;
                // One of ours, still showing its clock: the push takes its place.
                if (msg.clientId) {
                    const at = prev.findIndex((m) => m.local && m.clientId === msg.clientId);
                    if (at !== -1) {
                        const next = [...prev];
                        next[at] = msg;
                        return next;
                    }
                }
                return [...prev, msg];
            });
            setTypingPeople((prev) => prev.filter((p) => p.id !== idOf(msg.sender)));
        });

        const offDel = onSocket('task:comment-deleted', ({ _id }) => {
            setMessages((prev) => prev.filter((m) => m._id !== _id));
            setSelectedIds((prev) => prev.filter((x) => x !== _id));
        });

        const offEdit = onSocket('task:comment-edited', (updated) => {
            if (!mineThread(updated.taskId)) return;
            setMessages((prev) => prev.map((m) => (m._id === updated._id ? toMessage(updated) : m)));
        });

        const offReaction = onSocket('task:comment-reaction', ({ _id, taskId: tid, reactions }) => {
            if (!mineThread(tid)) return;
            setMessages((prev) => prev.map((m) => (m._id === _id ? { ...m, reactions } : m)));
        });

        // Somebody opened the thread: everything not theirs is now read by them.
        const offRead = onSocket('task:comment-read', ({ taskId: tid, userId, at }) => {
            if (!mineThread(tid)) return;
            setMessages((prev) => prev.map((m) => (
                idOf(m.sender) === String(userId)
                    || m.readBy.some((r) => idOf(r.user) === String(userId))
                    ? m
                    : { ...m, readBy: [...m.readBy, { user: userId, at }] }
            )));
        });

        // Somebody came online: these messages of mine have reached them.
        const offDelivered = onSocket('task:comment-delivered', ({ taskId: tid, commentIds, userId, at }) => {
            if (!mineThread(tid)) return;
            const ids = new Set((commentIds || []).map(String));
            setMessages((prev) => prev.map((m) => (
                ids.has(String(m._id)) && !m.deliveredTo.some((d) => idOf(d.user) === String(userId))
                    ? { ...m, deliveredTo: [...m.deliveredTo, { user: userId, at }] }
                    : m
            )));
        });

        const timers = typingTimers.current;
        const offTyping = onSocket('task:typing', ({ taskId: tid, userId, name, typing }) => {
            if (!mineThread(tid) || !name || String(userId) === myId) return;
            const id = String(userId);
            clearTimeout(timers.get(id));
            timers.delete(id);
            setTypingPeople((prev) => (typing
                ? [...prev.filter((p) => p.id !== id), { id, name }]
                : prev.filter((p) => p.id !== id)));
            if (typing) {
                timers.set(id, setTimeout(() => {
                    timers.delete(id);
                    setTypingPeople((prev) => prev.filter((p) => p.id !== id));
                }, 5000));
            }
        });

        return () => {
            socket.emit('task:leave', taskId);
            offNew(); offDel(); offEdit(); offReaction(); offRead(); offDelivered(); offTyping();
            timers.forEach(clearTimeout);
            timers.clear();
        };
    }, [taskId, myId]);

    // Escape leaves select mode.
    useEffect(() => {
        if (!selectMode) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !forwardIds) { setSelectMode(false); setSelectedIds([]); }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [selectMode, forwardIds]);

    /* --------------------------- scroll & read --------------------------- */

    // Only a new message scrolls, and only a reader already at the bottom.
    useEffect(() => {
        const grew = messages.length > lastCountRef.current;
        const first = lastCountRef.current === 0;
        lastCountRef.current = messages.length;
        if (grew && (first || atBottomRef.current) && threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (typingPeople.length && atBottomRef.current) bottomRef.current?.scrollIntoView({ block: 'end' });
    }, [typingPeople]);

    const onScroll = () => {
        const el = threadRef.current;
        if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };

    // Read once the thread has actually been on screen — the blue ticks.
    // Only people on the task leave receipts (the server ignores anyone else).
    const onTask = people.some((p) => idOf(p.user) === myId);
    const unreadForMe = onTask && messages.some((m) => !m.local
        && idOf(m.sender) !== myId
        && !m.readBy.some((r) => idOf(r.user) === myId));
    useEffect(() => {
        if (loading || !unreadForMe) return undefined;
        const t = setTimeout(() => {
            if (document.hidden) return;
            api.post(`${base}/read`).catch(() => { /* a failed receipt is not worth surfacing */ });
        }, 600);
        return () => clearTimeout(t);
    }, [loading, unreadForMe, messages.length, base]);

    /* ------------------------------ sending ------------------------------ */

    /*
     * WhatsApp-style, as in the chat: in the thread the instant Send is
     * pressed with a clock where the ticks go, delivered one at a time in the
     * order written, and kept with Retry if the server refuses it.
     */
    const sendQueue = useRef(Promise.resolve());
    const uploadAttempts = useRef(new Map());
    const uploadControllers = useRef(new Map());
    const cancellingUploads = useRef(new Set());

    const deliver = useCallback(async (local, attempt) => {
        if (uploadAttempts.current.get(local.clientId) !== attempt) return;
        const { text, files, audioMeta, mentions, replyToId } = local.outgoing;
        const data = new FormData();
        data.append('message', text);
        data.append('clientId', local.clientId);
        files.forEach((f) => data.append('attachments', f));
        if (audioMeta) {
            data.append('waveform', JSON.stringify(audioMeta.waveform || []));
            data.append('durationMs', String(audioMeta.durationMs || 0));
        }
        if (mentions?.length) data.append('mentions', JSON.stringify(mentions));
        if (replyToId) data.append('replyToId', replyToId);

        const controller = new AbortController();
        const uploadId = files.length ? newUploadId() : null;
        uploadControllers.current.set(local.clientId, { controller, uploadId });
        if (files.length) setMessages((prev) => prev.map((m) => m.local && m.clientId === local.clientId
            ? { ...m, uploadState: 'uploading', uploadProgress: null } : m));

        try {
            const res = await api.post(base, data, {
                headers: { 'Content-Type': 'multipart/form-data', ...(uploadId ? { 'x-upload-id': uploadId } : {}) },
                signal: controller.signal,
                onUploadProgress: files.length ? (event) => {
                    if (uploadAttempts.current.get(local.clientId) !== attempt || cancellingUploads.current.has(local.clientId)) return;
                    const progress = event.total ? Math.min(100, Math.round(event.loaded * 100 / event.total)) : null;
                    setMessages((prev) => prev.map((m) => m.local && m.clientId === local.clientId
                        ? { ...m, uploadProgress: progress, uploadState: progress === 100 ? 'processing' : 'uploading' } : m));
                } : undefined
            });
            const stored = toMessage(res.data);
            setMessages((prev) => {
                const arrived = prev.some((m) => m._id === stored._id);
                return prev.flatMap((m) => {
                    if (!(m.local && m.clientId === local.clientId)) return [m];
                    return arrived ? [] : [stored];
                });
            });
        } catch (err) {
            if (uploadAttempts.current.get(local.clientId) !== attempt) return;
            setMessages((prev) => prev.map((m) => (
                m.local && m.clientId === local.clientId
                    ? { ...m, pending: false, failed: true, error: err.response?.data?.message || 'Could not send.' }
                    : m
            )));
        } finally {
            if (uploadControllers.current.get(local.clientId)?.controller === controller) uploadControllers.current.delete(local.clientId);
            cancellingUploads.current.delete(local.clientId);
        }
    }, [base]);

    const enqueue = useCallback((local) => {
        const attempt = (uploadAttempts.current.get(local.clientId) || 0) + 1;
        uploadAttempts.current.set(local.clientId, attempt);
        sendQueue.current = sendQueue.current.then(() => deliver(local, attempt));
    }, [deliver]);

    const send = ({ text, files, audioMeta, mentions, mentionPeople = [], replyToId }) => {
        const clientId = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        const quoting = replyToId && replyTo && String(replyTo._id) === String(replyToId) ? replyTo : null;

        const local = {
            _id: `local:${clientId}`,
            clientId,
            local: true,
            pending: true,
            uploadState: files.length ? 'queued' : undefined,
            uploadProgress: null,
            taskId,
            sender: { _id: myId, name: me.name },
            author: { _id: myId, name: me.name },
            text,
            attachments: files.map((f) => ({ fileName: audioMeta ? 'Voice note' : f.name })),
            mentions: mentionPeople,
            replyTo: quoting
                ? { messageId: quoting._id, text: snippetOf(quoting), senderName: quoting.sender?.name || '' }
                : undefined,
            readBy: [],
            deliveredTo: [],
            reactions: [],
            createdAt: new Date().toISOString(),
            outgoing: { text, files, audioMeta, mentions, replyToId }
        };

        atBottomRef.current = true;
        setMessages((prev) => [...prev, local]);
        enqueue(local);
    };

    const retry = (message) => {
        setMessages((prev) => prev.map((m) => (
            m._id === message._id ? { ...m, pending: true, failed: false, error: '', uploadState: 'queued', uploadProgress: null } : m
        )));
        enqueue(message);
    };

    const markUploadCancelled = (message) => {
        uploadAttempts.current.set(message.clientId, (uploadAttempts.current.get(message.clientId) || 0) + 1);
        setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
            ? { ...m, pending: false, failed: true, error: 'Upload cancelled.' } : m));
    };

    const cancelUpload = async (message) => {
        if (!message.local || !message.pending || !message.outgoing?.files?.length || cancellingUploads.current.has(message.clientId)) return;
        const active = uploadControllers.current.get(message.clientId);
        if (!active) {
            markUploadCancelled(message);
            return;
        }
        cancellingUploads.current.add(message.clientId);
        setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
            ? { ...m, uploadState: 'cancelling' } : m));
        try {
            await requestUploadCancellation(active.uploadId);
            if (uploadControllers.current.get(message.clientId) !== active) return;
            markUploadCancelled(message);
            active.controller.abort();
        } catch (err) {
            if (uploadControllers.current.get(message.clientId) !== active) return;
            if (!err.response && message.uploadProgress !== 100) {
                markUploadCancelled(message);
                active.controller.abort();
            } else {
                setMessages((prev) => prev.map((m) => m.local && m.clientId === message.clientId
                    ? { ...m, uploadState: message.uploadProgress === 100 ? 'processing' : 'uploading' } : m));
                Swal.fire('Could not cancel', 'The server may already be saving this file. Waiting for its result.', 'info');
            }
        } finally {
            cancellingUploads.current.delete(message.clientId);
        }
    };

    const discard = (message) => setMessages((prev) => prev.filter((m) => m._id !== message._id));

    const emitTyping = (typing) => {
        getSocket()?.emit('task:typing', { taskId, name: me.name, typing });
    };

    /* ------------------------------ actions ------------------------------ */

    const saveEdit = async (message, text) => {
        if (text === (message.text || '')) { setEditing(null); return; }
        try {
            const res = await api.put(`${base}/${message._id}`, { message: text });
            setMessages((prev) => prev.map((m) => (m._id === message._id ? toMessage(res.data) : m)));
            setEditing(null);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not edit the message.', 'error');
        }
    };

    const isMine = (m) => idOf(m.sender) === myId;
    // Your own words, or Admin/HR moderating — the server's rule.
    const canDelete = (m) => isMine(m) || PRIVILEGED.includes(myRole);

    const remove = async (message) => {
        const ok = await Swal.fire({
            title: 'Delete this message?', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Delete'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.delete(`${base}/${message._id}`);
            setMessages((prev) => prev.filter((m) => m._id !== message._id));
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not delete the message.', 'error');
        }
    };

    /** React, or take a reaction back. Shown at once, then replaced by what the server stored. */
    const react = async (message, emoji) => {
        const before = message.reactions || [];
        setMessages((prev) => prev.map((m) => (
            m._id === message._id
                ? { ...m, reactions: applyReactionLocal(m.reactions || [], { _id: myId, name: 'You' }, emoji) }
                : m
        )));
        try {
            const res = await api.post(`${base}/${message._id}/react`, { emoji });
            setMessages((prev) => prev.map((m) => (m._id === message._id ? { ...m, reactions: res.data.reactions } : m)));
        } catch (err) {
            setMessages((prev) => prev.map((m) => (m._id === message._id ? { ...m, reactions: before } : m)));
            Swal.fire('Error', err.response?.data?.message || 'Could not save your reaction.', 'error');
        }
    };

    const jumpTo = (id) => {
        const el = document.getElementById(`dmsg-${id}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'background .4s';
        el.style.background = 'rgba(18,140,126,.12)';
        setTimeout(() => { el.style.background = ''; }, 1200);
    };

    /* ----------------------------- select mode ----------------------------- */

    const startSelect = (message) => {
        setSelectMode(true);
        setSelectedIds([message._id]);
        setEditing(null);
    };

    const toggleSelect = (message) => {
        setSelectedIds((prev) => (
            prev.includes(message._id) ? prev.filter((x) => x !== message._id) : [...prev, message._id]
        ));
    };

    const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };

    const selected = messages.filter((m) => selectedIds.includes(m._id));

    const copySelected = () => {
        if (!selected.length) return;
        if (selected.length === 1) {
            copyText(snippetOf(selected[0]));
        } else {
            copyText(
                transcript(selected.map((m) => ({
                    at: m.createdAt, name: m.sender?.name || 'Unknown', text: snippetOf(m)
                }))),
                `Copied ${selected.length} messages`
            );
        }
        exitSelect();
    };

    const deleteSelected = async () => {
        if (!selected.length || !selected.every(canDelete)) return;
        const ok = await Swal.fire({
            title: `Delete ${selected.length} message${selected.length > 1 ? 's' : ''}?`, icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Delete'
        });
        if (!ok.isConfirmed) return;

        const results = await Promise.allSettled(selected.map((m) => api.delete(`${base}/${m._id}`)));
        const gone = new Set(selected.filter((_, i) => results[i].status === 'fulfilled').map((m) => m._id));
        setMessages((prev) => prev.filter((m) => !gone.has(m._id)));

        const failed = results.length - gone.size;
        if (failed) {
            Swal.fire('Some were not deleted', `${failed} message${failed > 1 ? 's' : ''} could not be deleted.`, 'error');
        }
        exitSelect();
    };

    /* ------------------------------- render ------------------------------- */

    const members = people;
    const isGroupThread = members.length > 2;
    // Names on the bubbles only when there is more than one other voice — or
    // when the reader is not one of the two people talking.
    const namesMatter = isGroupThread || !onTask;
    const posted = messages.filter((m) => !m.local).length;

    let lastDay = null;
    let lastSender = null;

    // For the floating player: a voice note here links back to its task (or schedule) page.
    const voiceOrigin = {
        title: basePath === '/tasks/recurring' ? 'Recurring task discussion' : 'Task discussion',
        href: basePath === '/tasks/recurring' ? `/tasks/recurring/${taskId}` : `/task/${taskId}`
    };

    return (
        <VoiceOriginContext.Provider value={voiceOrigin}>
        <FileDropZone
            as="section"
            className="td-discussion td-chat"
            disabled={!!editing}
            onFiles={(files) => composerRef.current?.addFiles(files)}
            label="Drop files in this discussion"
        >
            {selectMode ? (
                <header className="td-panel-head dc-selecting">
                    <SelectionBar
                        count={selectedIds.length}
                        onCancel={exitSelect}
                        actions={[
                            { key: 'copy', label: 'Copy', icon: faCopy, onClick: copySelected },
                            { key: 'forward', label: 'Forward', icon: faShare, onClick: () => setForwardIds(selectedIds) },
                            {
                                key: 'delete',
                                label: 'Delete',
                                icon: faTrash,
                                danger: true,
                                onClick: deleteSelected,
                                disabled: !selected.every(canDelete),
                                title: selected.every(canDelete) ? 'Delete' : 'You can only delete your own messages'
                            }
                        ]}
                    />
                </header>
            ) : (
                <header className="td-panel-head">
                    <h2><FontAwesomeIcon icon={faComments} /> {title}</h2>
                    {posted > 0 && <span className="td-count">{posted}</span>}
                </header>
            )}

            <div className="msgr-main is-embedded">
                <div className="msgr-thread" ref={threadRef} onScroll={onScroll}>
                    {loading && (
                        <div className="msgr-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
                    )}

                    {!loading && messages.length === 0 && (
                        <div className="td-thread-empty">
                            <FontAwesomeIcon icon={faComments} className="td-empty-icon" />
                            <p>No messages yet.</p>
                            <span>Ask a question or post an update — everyone on this task will be notified.</span>
                        </div>
                    )}

                    {messages.map((m) => {
                        const day = daySeparator(m.createdAt);
                        const showDay = day !== lastDay;
                        lastDay = day;

                        const senderId = idOf(m.sender);
                        const mine = senderId === myId;
                        const firstOfRun = senderId !== lastSender || showDay;
                        lastSender = senderId;

                        return (
                            <React.Fragment key={m._id}>
                                {showDay && <div className="msgr-daysep">{day}</div>}
                                <MessageBubble
                                    id={`dmsg-${m._id}`}
                                    message={m}
                                    mine={mine}
                                    myId={myId}
                                    showAuthor={namesMatter && firstOfRun}
                                    firstOfRun={firstOfRun}
                                    tick={mine ? tickState(m, members, myId) : null}
                                    onInfo={mine ? setInfoFor : null}
                                    onRetry={retry}
                                    onDiscard={discard}
                                    onCancelUpload={cancelUpload}
                                    onReply={setReplyTo}
                                    onEdit={setEditing}
                                    onDelete={canDelete(m) ? remove : null}
                                    onJumpTo={jumpTo}
                                    onReact={react}
                                    onForward={(msg) => setForwardIds([msg._id])}
                                    onSelect={startSelect}
                                    selectMode={selectMode}
                                    selected={selectedIds.includes(m._id)}
                                    onToggleSelect={toggleSelect}
                                />
                            </React.Fragment>
                        );
                    })}

                    <TypingIndicator people={typingPeople.map((p) => ({
                        ...p,
                        ...(members.find((mm) => idOf(mm.user) === p.id)?.user || {})
                    }))} />
                    <div ref={bottomRef} />
                </div>

                <MessageComposer
                    ref={composerRef}
                    members={members}
                    myId={myId}
                    replyTo={replyTo}
                    onCancelReply={() => setReplyTo(null)}
                    editing={editing}
                    onCancelEdit={() => setEditing(null)}
                    onSend={send}
                    onSaveEdit={saveEdit}
                    onTyping={emitTyping}
                />
            </div>

            {infoFor && (
                <MessageInfo
                    message={infoFor}
                    isGroup={isGroupThread}
                    infoUrl={`${base}/${infoFor._id}/info`}
                    threadId={taskId}
                    receiptEvents={RECEIPT_EVENTS}
                    onClose={() => setInfoFor(null)}
                />
            )}

            {forwardIds && (
                <ForwardModal
                    commentIds={forwardIds}
                    onClose={() => setForwardIds(null)}
                    onForwarded={() => { setForwardIds(null); exitSelect(); }}
                />
            )}
        </FileDropZone>
        </VoiceOriginContext.Provider>
    );
};

export default TaskDiscussion;
